import globalMessage from "@/utils/globalMessage";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Col, Form, Input, Modal, Row, Spin } from "antd";
import moment from "moment";
import { useSelector } from "react-redux";

import DetailTabs from "@components/DetailTabs";
import RecordAuditTimeline from "@components/Table/RecordAuditLog";
import { api } from "@redux/api";
import { useAuth, AUTH_LEVEL } from "@/hooks/use-auth";
import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import FunctionalFormFieldsEditor, {
  normalizeFieldsEditorValue,
} from "@containers/management/FunctionalForm/FunctionalFormFieldsEditor";

const LIST_PATH = "/admin/equipment-category-template";
const DETAIL_PATH = "/admin/equipment-category-template-detail";

const normalizeEquipmentCategoryTemplateFields = (fields) => {
  const normalized = normalizeFieldsEditorValue(
    fields || { rows: [], columns: [], cells: {} }
  );
  return {
    ...normalized,
    columns: normalized.columns.map((column, index) => ({
      ...column,
      id: String(column?.id || `equipment_category_column_${index + 1}`),
      label: String(column?.label || "").trim() || (index === 0 ? "Value" : `Column ${index + 1}`),
    })),
  };
};

const EquipmentCategoryTemplateDetail = ({ isAdd = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { closeTab, refreshTab } = useKeepAliveContext();
  const canWrite = useAuth("equipmentCategoryTemplate", AUTH_LEVEL.WRITE);
  const canFullAccess = useAuth("equipmentCategoryTemplate", AUTH_LEVEL.FULL_ACCESS);
  const userName = useSelector((state) =>
    state?.Auth?.user || `${state?.Auth?.first_name || ""} ${state?.Auth?.last_name || ""}`
  );

  const isCreatePage = isAdd === true;
  const [loading, setLoading] = useState(!isCreatePage);
  const [readOnly, setReadOnly] = useState(!isCreatePage);
  const [detailData, setDetailData] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState("1");

  useEffect(() => {
    if (isCreatePage) {
      form.setFieldsValue({
        fields: { rows: [], columns: [], cells: {} },
      });
      setLoading(false);
      return;
    }
    if (id) fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isCreatePage]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const response = await api.getEquipmentCategoryTemplate(id);
      if (response.status === 200 && response.data) {
        const data = response.data;
        setDetailData(data);
        form.setFieldsValue({
          ...data,
          fields: normalizeEquipmentCategoryTemplateFields(data.fields),
          created_date: data.created_date ? moment(data.created_date).format("YYYY-MM-DD HH:mm") : "",
          modified_date: data.modified_date ? moment(data.modified_date).format("YYYY-MM-DD HH:mm") : "",
        });
      } else {
        globalMessage.error({ message: "Error", description: "Template not found" });
      }
    } catch (error) {
      console.error("Error fetching equipment category template:", error);
      globalMessage.error({ message: "Error", description: "Failed to load template details" });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setReadOnly(true);
    if (!detailData) return;
    form.setFieldsValue({
      ...detailData,
      fields: normalizeEquipmentCategoryTemplateFields(detailData.fields),
    });
  };

  const handleCancelCreate = () => {
    closeTab();
    navigate(LIST_PATH);
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const payload = {
        name: values.name,
        description: values.description || "",
        fields: normalizeEquipmentCategoryTemplateFields(values.fields),
      };
      let response;
      if (isCreatePage) {
        response = await api.createEquipmentCategoryTemplate(payload);
      } else {
        response = await api.editEquipmentCategoryTemplate(id, {
          ...payload,
          modifiedby_name: userName,
        });
      }

      if (response.status === 200 || response.status === 201) {
        globalMessage.success({
          message: "Success",
          description: isCreatePage ? "Template created successfully" : "Template updated successfully",
        });
        refreshTab(LIST_PATH);
        if (isCreatePage) {
          closeTab();
          navigate(LIST_PATH);
        } else {
          setReadOnly(true);
          fetchDetail();
        }
      }
    } catch (error) {
      console.error("Error saving equipment category template:", error);
      globalMessage.error({
        message: "Error",
        description: isCreatePage ? "Failed to create template" : "Failed to update template",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Are you sure you want to delete this template?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setLoading(true);
          const response = await api.deleteEquipmentCategoryTemplate(id);
          if (response.status === 204 || response.status === 200) {
            globalMessage.success({ message: "Success", description: "Template deleted successfully" });
            refreshTab(LIST_PATH);
            closeTab();
            navigate(LIST_PATH);
          }
        } catch (error) {
          console.error("Error deleting equipment category template:", error);
          globalMessage.error({ message: "Error", description: "Failed to delete template" });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleClone = () => {
    Modal.confirm({
      title: "Clone this template?",
      content: "A copy will be created as a new template.",
      okText: "Clone",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setLoading(true);
          const response = await api.cloneEquipmentCategoryTemplate(id);
          const newId = response.data?.id;
          if ((response.status === 200 || response.status === 201) && newId) {
            globalMessage.success({ message: "Success", description: "Template cloned successfully" });
            refreshTab(LIST_PATH);
            closeTab();
            navigate(`${DETAIL_PATH}/${newId}`);
          }
        } catch (error) {
          console.error("Error cloning equipment category template:", error);
          globalMessage.error({ message: "Error", description: "Failed to clone template" });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const renderOperationButton = () => {
    if (isCreatePage) {
      return (
        <div style={{ display: "flex", gap: 8 }}>
          <Button onClick={handleCancelCreate}>
            <i className="fa-solid fa-xmark pr-2" />Cancel
          </Button>
          <Button type="primary" onClick={() => form.submit()}>
            <i className="fa-solid fa-floppy-disk pr-2" />Save
          </Button>
        </div>
      );
    }
    if (readOnly) {
      return (
        <div style={{ display: "flex", gap: 8 }}>
          {canWrite && (
            <Button type="primary" onClick={() => setReadOnly(false)}>
              <i className="fa-solid fa-pen-to-square pr-2" />Edit
            </Button>
          )}
          {canWrite && (
            <Button onClick={handleClone}>
              <i className="fa-regular fa-copy pr-2" />Clone
            </Button>
          )}
          {canFullAccess && (
            <Button danger onClick={handleDelete}>
              <i className="fa-solid fa-trash pr-2" />Delete
            </Button>
          )}
        </div>
      );
    }
    return (
      <div style={{ display: "flex", gap: 8 }}>
        <Button type="primary" onClick={() => form.submit()}>
          <i className="fa-solid fa-floppy-disk pr-2" />Save
        </Button>
        <Button onClick={handleCancel}>Cancel</Button>
      </div>
    );
  };

  const renderFormFields = () => (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="horizontal"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      className="detailForm"
      autoComplete="off"
    >
      <Row gutter={[0, 0]}>
        {!isCreatePage && (
          <>
            <Col xs={24} lg={12}>
              <Form.Item label="ID" name="id">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={0} lg={12} />
          </>
        )}
        <Col xs={24} lg={12}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "This field is required" }]}>
            <Input disabled={readOnly && !isCreatePage} allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} lg={24}>
          <Form.Item
            className="functional-form-full-row-item"
            label="Description"
            name="description"
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
          >
            <Input.TextArea rows={2} disabled={readOnly && !isCreatePage} allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} lg={24}>
          <Form.Item
            className="functional-form-full-row-item"
            label="Fields"
            name="fields"
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
          >
            <FunctionalFormFieldsEditor readOnly={readOnly && !isCreatePage} />
          </Form.Item>
        </Col>
        {!isCreatePage && (
          <>
            <Col xs={24} lg={12}>
              <Form.Item label="Created By">
                <span>{detailData?.createdby_name}</span>
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="Created Date">
                <span>{detailData?.created_date ? moment(detailData.created_date).format("YYYY-MM-DD HH:mm") : ""}</span>
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="Modified By">
                <span>{detailData?.lastModified_name}</span>
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label="Modified Date">
                <span>{detailData?.modified_date ? moment(detailData.modified_date).format("YYYY-MM-DD HH:mm") : ""}</span>
              </Form.Item>
            </Col>
          </>
        )}
      </Row>
    </Form>
  );

  if (loading && !detailData && !isCreatePage) {
    return (
      <div style={{ textAlign: "center", padding: "100px 0" }}>
        <Spin size="large" />
      </div>
    );
  }

  const tabItems = [
    {
      key: "1",
      label: (
        <span>
          <i className="fa-regular fa-rectangle-list pr-2" />Detail
        </span>
      ),
      children: (
        <div className="slds-m-around_x-small">
          <div className="functional-form-detail-inner px-4">
            <div className="w-full">{renderFormFields()}</div>
          </div>
        </div>
      ),
    },
    ...(!isCreatePage
      ? [
          {
            key: "2",
            label: (
              <span>
                <i className="fa-solid fa-clock-rotate-left pr-2" />Audit Log
              </span>
            ),
            children: <RecordAuditTimeline moduleId={id} module="EquipmentCategoryTemplate" />,
          },
        ]
      : []),
  ];

  return (
    <div className="functional-form-detail-container">
      <Spin spinning={loading}>
        <DetailTabs
          defaultActiveKey="1"
          destroyInactiveTabPane
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          tabBarExtraContent={activeTabKey === "1" ? renderOperationButton() : null}
          mobileThreshold={1}
          items={tabItems}
        />
      </Spin>
    </div>
  );
};

export default EquipmentCategoryTemplateDetail;
